import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  ArrowUpRight,
  ChevronDown,
  ArrowUp,
  Linkedin,
  Facebook
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Footer = () => {
  const [activeSection, setActiveSection] = useState(null);
  const location = useLocation();

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  // Enlaces alineados con la estructura real del sitio
  const companyLinks = [
    { to: '/nosotros', label: 'Sobre Nosotros' },
    { to: '/galeria', label: 'Portafolio de Proyectos' },
    { to: '/blog', label: 'Centro de Conocimiento' },
    { to: '/contacto', label: 'Contacto Empresarial' }
  ];

  // Servicios reales de Visioner7
  const serviceLinks = [
    { to: '/servicios', label: 'Desarrollo de Software' },
    { to: '/servicios', label: 'Facturación Electrónica' },
    { to: '/servicios', label: 'Aplicaciones Móviles' },
    { to: '/servicios', label: 'Consultoría Tecnológica' }
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Correo',
      text: 'contacto@visioner7.com',
      link: 'mailto:contacto@visioner7.com'
    },
    {
      icon: Phone,
      label: 'Teléfono',
      text: '+51 955 000 321',
      link: 'https://wa.me/51955000321'
    },
    {
      icon: MapPin,
      label: 'Oficina',
      text: 'Calle Octavio Muñoz Najar 227, Lima',
      link: 'https://maps.google.com'
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const easePremium = [0.22, 1, 0.36, 1];

  return (
    <footer className="bg-slate-950 text-slate-400 relative overflow-hidden">
      
      {/* Textura de fondo ultra-sutil (consistente con el resto del sitio) */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(#10b981 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10 pt-20 pb-8">
        
        {/* ========================================== */}
        {/* GRID PRINCIPAL                             */}
        {/* ========================================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {/* Columna 1: Marca y Descripción */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: easePremium }}
            className="lg:col-span-1"
          >
            <Link to="/" className="flex items-center gap-2.5 mb-6 group">
              <img 
                src="/images/logo22.png" 
                alt="Visioner7" 
                className="h-9 w-auto brightness-0 invert" 
              />
              <span className="text-2xl font-bold text-white tracking-tight group-hover:text-emerald-400 transition-colors duration-300">
                Visioner7
              </span>
            </Link>
            <p className="text-sm leading-relaxed mb-8 max-w-xs">
              Desarrollamos soluciones tecnológicas a medida que optimizan procesos, 
              automatizan tareas y potencian el crecimiento sostenible de su empresa.
            </p>
            
            {/* Redes Sociales */}
            <div className="flex items-center gap-4">
              <a 
                href="#" 
                className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="#" 
                className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          {/* Columna 2: Empresa */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6, ease: easePremium }}
          >
            {/* Desktop Title */}
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-6 hidden md:block">
              Empresa
            </h4>
            
            {/* Mobile Accordion */}
            <button 
              className="md:hidden flex items-center justify-between w-full text-sm font-bold text-white uppercase tracking-wider mb-4"
              onClick={() => toggleSection('company')}
            >
              Empresa
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeSection === 'company' ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence initial={false}>
              {(activeSection === 'company' || window.innerWidth >= 768) && (
                <motion.ul 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  {companyLinks.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="group flex items-center text-sm hover:text-white transition-colors duration-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Columna 3: Servicios */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6, ease: easePremium }}
          >
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-6 hidden md:block">
              Soluciones
            </h4>
            
            <button 
              className="md:hidden flex items-center justify-between w-full text-sm font-bold text-white uppercase tracking-wider mb-4"
              onClick={() => toggleSection('services')}
            >
              Soluciones
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeSection === 'services' ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence initial={false}>
              {(activeSection === 'services' || window.innerWidth >= 768) && (
                <motion.ul 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  {serviceLinks.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="group flex items-center text-sm hover:text-white transition-colors duration-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Columna 4: Contacto */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6, ease: easePremium }}
          >
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-6 hidden md:block">
              Contacto
            </h4>

            <button 
              className="md:hidden flex items-center justify-between w-full text-sm font-bold text-white uppercase tracking-wider mb-4"
              onClick={() => toggleSection('contact')}
            >
              Contacto
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeSection === 'contact' ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence initial={false}>
              {(activeSection === 'contact' || window.innerWidth >= 768) && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-5 overflow-hidden"
                >
                  {contactInfo.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <a 
                        key={index}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-start gap-3 hover:text-white transition-colors duration-200"
                      >
                        <div className="mt-0.5 w-5 h-5 rounded bg-slate-900 border border-slate-800 flex items-center justify-center flex-shrink-0 group-hover:border-emerald-500/50 group-hover:bg-emerald-500/10 transition-all duration-300">
                          <Icon className="w-3 h-3 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-0.5">
                            {item.label}
                          </p>
                          <p className="text-sm font-medium text-slate-300 group-hover:text-emerald-400 transition-colors">
                            {item.text}
                          </p>
                        </div>
                      </a>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* ========================================== */}
        {/* BARRA INFERIOR (Copyright y Legales)       */}
        {/* ========================================== */}
        <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          
          <div className="text-sm text-slate-500">
            <p>© {new Date().getFullYear()} Visioner7. Todos los derechos reservados.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm text-slate-500">
            <a href="#" className="hover:text-white transition-colors duration-200">Política de Privacidad</a>
            <a href="#" className="hover:text-white transition-colors duration-200">Términos de Servicio</a>
            
            <button 
              onClick={scrollToTop}
              className="flex items-center gap-1.5 text-slate-500 hover:text-emerald-400 transition-colors duration-200 group ml-0 sm:ml-4 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-900 sm:border-none"
            >
              Volver arriba
              <ArrowUp className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;